'use client'
import type { ChainId } from '@morpho-org/blue-sdk'

import { memo, useCallback, useMemo } from 'react'

import type { DashboardEarnPositionsFilters } from '@/helpers/filter/table'
import type { UserEarnPositionFacets } from '@/hooks/positions/userEarnPositionsQuery'

import { AdvancedAssetFilter, TableNetworkFilter } from '@/components/common/TableAdvancedFilters'
import { userPositionFacetsToAssetItems } from '@/helpers/filter/vaultMasterFilter/getFacetFilters'

interface DashboardEarnPositionsFiltersProps {
  filters?: DashboardEarnPositionsFilters
  onFiltersChange?: (filters: DashboardEarnPositionsFilters) => void
  disabled?: boolean
  positionChainIds?: ChainId[]
  facets?: UserEarnPositionFacets
}

const DashboardEarnPositionsFilters = ({
  filters,
  onFiltersChange,
  disabled = false,
  positionChainIds,
  facets
}: DashboardEarnPositionsFiltersProps) => {
  const assets = useMemo(() => userPositionFacetsToAssetItems(facets?.assets), [facets])

  const handleAssetIdsFilterChange = useCallback(
    (assetIdsFilter: string[]) => {
      onFiltersChange?.({
        ...filters,
        assetIdsFilter,
        chainIdsFilter: filters?.chainIdsFilter ?? []
      })
    },
    [onFiltersChange, filters]
  )

  const handleChainIdsFilterChange = useCallback(
    (chainIdsFilter: ChainId[]) => {
      onFiltersChange?.({
        ...filters,
        chainIdsFilter,
        // Reset asset filter when network filter changes
        assetIdsFilter: []
      })
    },
    [onFiltersChange, filters]
  )

  return (
    <div className="gap-lg max-sm:gap-x-lg max-sm:gap-y-sm flex flex-row flex-wrap items-center">
      <TableNetworkFilter
        chainIdsFilter={filters?.chainIdsFilter ?? []}
        handleChainIdsFilterChange={handleChainIdsFilterChange}
        disabled={disabled || !facets}
        availableChains={positionChainIds}
        page="dashboard_earn_positions"
      />
      <AdvancedAssetFilter
        label="asset"
        trackingLabel="deposit"
        assets={assets}
        assetIdsFilter={filters?.assetIdsFilter ?? []}
        chainIdsFilter={filters?.chainIdsFilter ?? []}
        handleAssetIdsFilterChange={handleAssetIdsFilterChange}
        disabled={disabled || !facets}
        page="dashboard_earn_positions"
      />
    </div>
  )
}

export default memo(DashboardEarnPositionsFilters)
